import {
    Component,
    ElementRef,
    EventEmitter,
    NgZone,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';

export interface FotoVisor {
    nombre: string;
    descripcion?: string | null;
    urlVer: string;
    urlDescarga: string;
}

/**
 * Visor de fotos a pantalla completa: ampliar, moverse por la foto, pasar a la
 * siguiente y descargar.
 *
 * No usa el modal de Bootstrap como el resto de la aplicación. Un modal deja
 * márgenes, cabecera y pie, y aquí la foto tiene que ocupar la pantalla entera:
 * el operario está mirando una pieza en una tablet, no leyendo un diálogo. Al
 * no depender de jQuery, además, el componente se puede montar en un test.
 *
 * El zoom está hecho a mano porque no queda otra: `src/index.html` fija
 * `user-scalable=no` en el viewport, así que el navegador no da pellizco. Se
 * escucha `pointer*`, que en un mismo juego de eventos cubre el dedo y el ratón
 * —útil para depurar en el portátil— y no arrastra ninguna dependencia.
 */
@Component({
    selector: 'app-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.scss'],
    standalone: false
})
export class ImageViewerComponent implements OnDestroy {
    @Output() cerrado = new EventEmitter<void>();

    abierto = false;
    fotos: FotoVisor[] = [];
    indice = 0;

    escala = 1;
    despX = 0;
    despY = 0;
    cargando = true;
    fallo = false;

    private readonly ESCALA_MIN = 1;
    private readonly ESCALA_MAX = 5;
    /** Un dedo tiene que recorrer esto para que cuente como pasar de foto. */
    private readonly UMBRAL_DESLIZAR = 60;

    constructor(private zone: NgZone) {}

    get foto(): FotoVisor | undefined {
        return this.fotos[this.indice];
    }

    get ampliada() {
        return this.escala > 1.01;
    }

    abrir(fotos: FotoVisor[], indice = 0) {
        if (!fotos.length) return;

        this.fotos = fotos;
        this.indice = Math.min(Math.max(indice, 0), fotos.length - 1);
        this.abierto = true;
        this.reiniciarZoom();
        this.cargando = true;
        this.fallo = false;
    }

    cerrar() {
        this.abierto = false;
        this.soltarLienzo();
        this.cerrado.emit();
    }

    imagenCargada() {
        this.cargando = false;
        this.fallo = false;
    }

    imagenFallida() {
        this.cargando = false;
        this.fallo = true;
    }

    // -- navegación -------------------------------------------------------

    ir(salto: number) {
        const siguiente = this.indice + salto;
        if (siguiente < 0 || siguiente >= this.fotos.length) return;

        this.indice = siguiente;
        this.reiniciarZoom();
        this.cargando = true;
        this.fallo = false;
    }

    get haySiguiente() {
        return this.indice < this.fotos.length - 1;
    }

    get hayAnterior() {
        return this.indice > 0;
    }

    // -- zoom y arrastre --------------------------------------------------

    private punteros = new Map<number, { x: number; y: number }>();
    private distanciaInicial = 0;
    private escalaInicial = 1;
    private centroInicial = { x: 0, y: 0 };
    private despInicial = { x: 0, y: 0 };
    private inicioArrastre = { x: 0, y: 0 };
    private ultimoToque = 0;

    private lienzo?: HTMLElement;
    private quitarListeners?: () => void;

    /**
     * El lienzo se recibe por un setter y no por un `@ViewChild` normal porque
     * cuelga de un `*ngIf`: cada apertura del visor crea un elemento NUEVO, y
     * un `@ViewChild` resuelto en `ngAfterViewInit` se quedaría apuntando al
     * primero (o a nada, si el visor arranca cerrado).
     */
    @ViewChild('lienzo') set refLienzo(el: ElementRef<HTMLElement> | undefined) {
        this.soltarLienzo();

        this.lienzo = el?.nativeElement;
        if (!this.lienzo) return;

        // `pointermove` va fuera de Angular: durante un pellizco llegan
        // decenas por segundo y disparar la detección de cambios en cada uno
        // deja la tablet a tirones. La plantilla se refresca a mano, y solo
        // cuando algo cambia de verdad.
        //
        // Y va con `addEventListener` en vez de con un binding de plantilla
        // porque hace falta `{ passive: false }`: sin eso el navegador ignora
        // el `preventDefault()` y se lleva el gesto como scroll.
        const elemento = this.lienzo;
        const mover = (e: PointerEvent) => this.alMover(e);

        this.zone.runOutsideAngular(() => {
            elemento.addEventListener('pointermove', mover, { passive: false });
        });

        this.quitarListeners = () => {
            elemento.removeEventListener('pointermove', mover);
        };
    }

    private soltarLienzo() {
        this.quitarListeners?.();
        this.quitarListeners = undefined;
        this.lienzo = undefined;
        this.punteros.clear();
    }

    ngOnDestroy() {
        this.soltarLienzo();
    }

    alPulsar(e: PointerEvent) {
        // El dedo se apunta ANTES de capturarlo. `setPointerCapture` lanza si
        // el puntero ya no está activo, y si eso ocurriera aquí arriba el dedo
        // no llegaría a registrarse: el gesto entero se perdería en silencio.
        this.punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });

        try {
            (e.target as HTMLElement)?.setPointerCapture?.(e.pointerId);
        } catch {
            // La captura es una comodidad —permite seguir el dedo aunque se
            // salga del elemento—, no un requisito: sin ella el gesto sigue
            // funcionando mientras el dedo esté encima.
        }

        if (this.punteros.size === 2) {
            const [a, b] = [...this.punteros.values()];
            this.distanciaInicial = this.distancia(a, b);
            this.escalaInicial = this.escala;
            this.centroInicial = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            this.despInicial = { x: this.despX, y: this.despY };
            return;
        }

        this.inicioArrastre = { x: e.clientX, y: e.clientY };
        this.despInicial = { x: this.despX, y: this.despY };
    }

    private alMover(e: PointerEvent) {
        if (!this.punteros.has(e.pointerId)) return;

        this.punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (this.punteros.size >= 2) {
            e.preventDefault();
            this.pellizcar();
            return;
        }

        if (!this.ampliada) return;

        // Con la foto ampliada el dedo la arrastra; sin ampliar el mismo gesto
        // es "pasar a la siguiente", y ese se resuelve al levantar el dedo.
        e.preventDefault();
        this.despX = this.despInicial.x + (e.clientX - this.inicioArrastre.x);
        this.despY = this.despInicial.y + (e.clientY - this.inicioArrastre.y);
        this.limitarDesplazamiento();
        this.repintar();
    }

    private pellizcar() {
        const [a, b] = [...this.punteros.values()];
        const distancia = this.distancia(a, b);
        if (!this.distanciaInicial || !distancia) return;

        const anterior = this.escala;
        this.escala = this.acotar(
            (this.escalaInicial * distancia) / this.distanciaInicial,
            this.ESCALA_MIN,
            this.ESCALA_MAX
        );

        // El zoom tiene que ir hacia donde están los dedos, no hacia el centro
        // de la imagen: sin esto la foto se escapa mientras se amplía y hay que
        // perseguirla arrastrando.
        const centro = this.centroDelLienzo();
        const factor = this.escala / anterior;
        this.despX = centro.x + (this.despX - centro.x) * factor;
        this.despY = centro.y + (this.despY - centro.y) * factor;

        this.limitarDesplazamiento();
        this.repintar();
    }

    alSoltar(e: PointerEvent) {
        const inicio = this.inicioArrastre;
        const unSoloDedo = this.punteros.size === 1;
        this.punteros.delete(e.pointerId);

        if (this.punteros.size === 1) {
            // Se levanta un dedo del pellizco: el que queda pasa a arrastrar
            // desde donde está, o la foto pegaría un salto.
            const [queda] = [...this.punteros.values()];
            this.inicioArrastre = { x: queda.x, y: queda.y };
            this.despInicial = { x: this.despX, y: this.despY };
            return;
        }

        if (!unSoloDedo) return;

        const recorridoX = e.clientX - inicio.x;
        const recorridoY = e.clientY - inicio.y;

        // Un toque limpio, sin arrastre: puede ser el segundo de un doble
        // toque. Se mira SIEMPRE, también con la foto ampliada, porque el doble
        // toque es como se vuelve a verla entera. Con la comprobación de
        // `ampliada` por delante no había forma de salir del zoom con el dedo:
        // solo quedaba el botón de la barra.
        if (Math.abs(recorridoX) < 10 && Math.abs(recorridoY) < 10) {
            this.quizaDobleToque(e);
            return;
        }

        if (this.ampliada) return;

        // El recorrido tiene que ser MÁS horizontal que vertical: sin esa
        // comparación, un dedo que baja en diagonal —el gesto de quien recorre
        // la rejilla— cambiaría de foto sin querer.
        if (
            Math.abs(recorridoX) > this.UMBRAL_DESLIZAR &&
            Math.abs(recorridoX) > Math.abs(recorridoY)
        ) {
            this.ir(recorridoX < 0 ? 1 : -1);
        }
    }

    alCancelar(e: PointerEvent) {
        this.punteros.delete(e.pointerId);
    }

    private quizaDobleToque(e: PointerEvent) {
        const ahora = Date.now();

        if (ahora - this.ultimoToque < 300) {
            this.ultimoToque = 0;
            this.alternarZoom({ x: e.clientX, y: e.clientY });
            return;
        }

        this.ultimoToque = ahora;
    }

    /** Doble toque: amplía a la mitad del máximo, o vuelve a la foto entera. */
    alternarZoom(punto?: { x: number; y: number }) {
        if (this.ampliada) {
            this.reiniciarZoom();
            this.repintar();
            return;
        }

        const destino = Math.min(2.5, this.ESCALA_MAX);
        const centro = this.centroDelLienzo(punto);

        this.escala = destino;
        this.despX = centro.x * (1 - destino);
        this.despY = centro.y * (1 - destino);
        this.limitarDesplazamiento();
        this.repintar();
    }

    private reiniciarZoom() {
        this.escala = 1;
        this.despX = 0;
        this.despY = 0;
    }

    /**
     * Dónde está el gesto respecto al centro del lienzo. El `transform` tiene
     * el origen en el centro, así que las cuentas del zoom van en ese sistema.
     */
    private centroDelLienzo(punto?: { x: number; y: number }) {
        const caja = this.lienzo?.getBoundingClientRect();
        if (!caja) return { x: 0, y: 0 };

        const origen = punto ?? this.centroInicial;

        return {
            x: origen.x - caja.left - caja.width / 2,
            y: origen.y - caja.top - caja.height / 2,
        };
    }

    /**
     * Que no se pueda arrastrar la foto fuera de la pantalla.
     *
     * El margen se mide sobre la FOTO, no sobre el lienzo. La imagen va con
     * `object-fit: contain`, así que en el eje que le sobra ocupa bastante
     * menos que el hueco: una foto apaisada en una tablet vertical deja franjas
     * negras arriba y abajo. Midiendo con el lienzo, el margen vertical
     * permitido salía mayor que la propia foto y se podía empujar hasta
     * sacarla ENTERA de la pantalla, dejando al operario mirando el fondo.
     */
    private limitarDesplazamiento() {
        const caja = this.lienzo?.getBoundingClientRect();
        if (!caja) return;

        const foto = this.medirFoto(caja);

        // Cuánto sobresale la foto ampliada por cada lado. Si en un eje cabe
        // entera, el margen es cero y ahí no se mueve.
        const margenX = Math.max(0, (foto.ancho * this.escala - caja.width) / 2);
        const margenY = Math.max(0, (foto.alto * this.escala - caja.height) / 2);

        this.despX = this.acotar(this.despX, -margenX, margenX);
        this.despY = this.acotar(this.despY, -margenY, margenY);
    }

    /** Lo que ocupa la foto dentro del lienzo, ya aplicado el `contain`. */
    private medirFoto(caja: DOMRect) {
        const imagen = this.lienzo?.querySelector('img') as HTMLImageElement | null;
        const anchoNatural = imagen?.naturalWidth || 0;
        const altoNatural = imagen?.naturalHeight || 0;

        // Mientras la foto no ha cargado no se conoce su proporción; se toma el
        // lienzo entero, que es el comportamiento de antes y no deja arrastrar
        // de más.
        if (!anchoNatural || !altoNatural) {
            return { ancho: caja.width, alto: caja.height };
        }

        const escalaContain = Math.min(
            caja.width / anchoNatural,
            caja.height / altoNatural
        );

        return {
            ancho: anchoNatural * escalaContain,
            alto: altoNatural * escalaContain,
        };
    }

    private acotar(valor: number, minimo: number, maximo: number) {
        return Math.min(Math.max(valor, minimo), maximo);
    }

    private distancia(a: { x: number; y: number }, b: { x: number; y: number }) {
        return Math.hypot(a.x - b.x, a.y - b.y);
    }

    get transformacion() {
        return `translate(${this.despX}px, ${this.despY}px) scale(${this.escala})`;
    }

    /**
     * El `pointermove` corre fuera de Angular, así que el `transform` se aplica
     * a mano; si no, la foto no se movería hasta el siguiente ciclo por otra
     * causa.
     */
    private repintar() {
        const imagen = this.lienzo?.querySelector('img');
        if (imagen) (imagen as HTMLElement).style.transform = this.transformacion;
    }
}
